
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';

const lastUpdated = 'June 15, 2025';

const sections = [
  {
    title: 'Acceptance of Terms',
    content: [
      'By accessing or using the ig-night website and purchasing our products, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our website or services.',
      'We reserve the right to update or modify these terms at any time without prior notice. Your continued use of the site following any changes constitutes acceptance of those changes.',
    ],
  },
  {
    title: 'Products & Descriptions',
    content: [
      'We make every effort to display our candles and their colours, textures, and dimensions as accurately as possible. However, the actual appearance of products may vary slightly due to differences in screen settings, photography lighting, and the handmade nature of our concrete vessels.',
      'Each ig-night candle is hand-poured and unique. Minor variations in colour, texture, and weight are inherent to the craft and are not considered defects.',
    ],
  },
  {
    title: 'Pricing & Payment',
    content: [
      'All prices displayed on our website are in the currency indicated at checkout and are inclusive of applicable taxes unless otherwise stated. Shipping costs are calculated and displayed before you confirm your order.',
      'We reserve the right to change prices at any time. However, any price changes will not affect orders that have already been confirmed. Payment must be made in full at the time of purchase through our accepted payment methods.',
    ],
  },
  {
    title: 'Orders & Fulfilment',
    content: [
      'When you place an order, you will receive an email confirmation. This confirmation does not guarantee acceptance of your order. We reserve the right to refuse or cancel any order for reasons including product availability, pricing errors, or suspected fraudulent activity.',
      'Processing times are typically 2\u20135 business days. Shipping times vary depending on your location. We are not responsible for delays caused by carriers or customs processing.',
    ],
  },
  {
    title: 'Returns & Refunds',
    content: [
      'Due to the nature of our products, we accept returns only for items that arrive damaged or defective. If you receive a damaged product, please contact us within 7 days of delivery with photographs of the damage.',
      'Approved refunds will be processed to the original payment method within 10 business days. Shipping costs are non-refundable unless the return is due to our error.',
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      'All content on this website \u2014 including text, images, logos, product designs, and branding \u2014 is the property of ig-night and is protected by applicable intellectual property laws. You may not reproduce, distribute, or use any content without our prior written consent.',
      'The ig-night name, logo, and all related marks are trademarks of ig-night. Unauthorised use of these marks is strictly prohibited.',
    ],
  },
  {
    title: 'Candle Safety & Liability',
    content: [
      'Candles involve an open flame and must be used responsibly. Always follow the safety instructions included with your candle: never leave a burning candle unattended, keep away from flammable materials, and place on a heat-resistant surface.',
      'ig-night shall not be held liable for any injury, damage, or loss resulting from improper use, negligence, or failure to follow the provided safety guidelines.',
    ],
  },
  {
    title: 'Website Use & Conduct',
    content: [
      'You agree to use our website only for lawful purposes. You may not use the site in any way that could damage, disable, or impair its functionality, or interfere with other users\u2019 experience.',
      'You are prohibited from attempting to gain unauthorised access to any part of the website, its servers, or any connected databases. Any such attempt may result in legal action.',
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      'To the fullest extent permitted by law, ig-night shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or products, including but not limited to loss of data, revenue, or profits.',
      'Our total liability for any claim related to our products or services shall not exceed the amount you paid for the specific product giving rise to the claim.',
    ],
  },
  {
    title: 'Governing Law',
    content: [
      'These Terms of Service are governed by and construed in accordance with applicable laws. Any disputes arising from these terms or your use of our website shall be resolved through good-faith negotiation before pursuing formal legal proceedings.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'If you have any questions about these Terms of Service, please contact us at hello@ig-night.com. We are happy to clarify any aspect of these terms and assist you with any concerns.',
    ],
  },
];

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#0B1B3A]">
      {/* Header */}
      <header className="bg-[#0B1B3A] border-b border-[#162D52]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-serif text-[#F5F0E8] cursor-pointer"
            style={{ fontFamily: 'Pacifico, serif' }}
          >
            ig-night
          </Link>
          <Link
            to="/"
            className="text-sm text-[#8A9BB5] hover:text-[#F5F0E8] transition-colors uppercase tracking-wider cursor-pointer flex items-center gap-2"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-widest text-[#D4C5A9] uppercase mb-4">
            Legal
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#F5F0E8] mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Terms of Service
          </h1>
          <p className="text-sm text-[#8A9BB5]">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-sm sm:text-base text-[#8A9BB5] leading-relaxed mb-12">
            Welcome to <strong className="text-[#F5F0E8]">ig-night</strong>.
            These Terms of Service govern your use of our website and the
            purchase of our handcrafted concrete candles. Please read them
            carefully before placing an order or using any of our services.
          </p>

          <div className="space-y-10">
            {sections.map((section, i) => (
              <article key={i}>
                <h4>
                  <a
                    href={`#section-${i}`}
                    id={`section-${i}`}
                    className="text-lg sm:text-xl font-serif text-[#F5F0E8] mb-4 block"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {i + 1}. {section.title}
                  </a>
                </h4>
                <div className="space-y-3">
                  {section.content.map((paragraph, j) => (
                    <p
                      key={j}
                      className="text-sm sm:text-base text-[#8A9BB5] leading-relaxed"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-16 pt-8 border-t border-[#162D52] text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#D4C5A9] text-[#0B1B3A] px-6 py-3 rounded-md text-sm font-medium hover:bg-[#E8DCC4] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
